import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Alert, Button, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import GuideLink from '@/components/common/GuideLink';
import GeneralPool from '@/components/hiring/GeneralPool';
import PositionPipeline from '@/components/hiring/PositionPipeline';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import { usePreviewLock } from '@/shared/hooks/usePreviewLock';
import { positionService } from '@/shared/services/positionService';
import SortableTh from '@/components/common/SortableTh';
import SortBySelect from '@/components/common/SortBySelect';
import TableControlsBar from '@/components/common/TableControlsBar';
import TableEmpty from '@/components/common/TableEmpty';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { useTableControls } from '@/shared/hooks/useTableControls';
import { positionStatusText } from './positionDisplay';
import ListFilterNote from './ListFilterNote';
import { POSITION_LISTS, listFor } from './overviewLists';
import { useTableParams, useTabParam } from '@/shared/hooks/useTableParams';
import './CoordinatorPositionsTab.css';

// The table turns into cards below this width (CoordinatorPositionsTab.css),
// where the headers are not shown and a Sort by select takes over
const CARDS_QUERY = '(max-width: 991.98px)';

const PLACES_OPTIONS = [
    { value: 'open', label: 'Has available places' },
    { value: 'none', label: 'No available places' },
];

const placesOf = (position) => (position.openSlots > 0 ? 'open' : 'none');

const courseCodeOf = (p) => `${p.subject} ${p.courseNumber}`;

// The counts say open or filled; only these states add something they don't
const statusNoteOf = (p) =>
    ['pending', 'closed'].includes(p.status) ? positionStatusText(p) : null;

const NUMBER = { type: 'number', align: 'end', searchable: false };

// `inTable: false` is exported and searched but drawn inside the Course cell
const COLUMNS = [
    {
        key: 'course',
        label: 'Course and section',
        type: 'text',
        value: (p) => `${courseCodeOf(p)} ${p.section}`,
        csv: (p) =>
            [
                courseCodeOf(p),
                p.section && `Section ${p.section}`,
                p.title,
                statusNoteOf(p) && `(${statusNoteOf(p)})`,
            ]
                .filter(Boolean)
                .join(' '),
    },
    {
        key: 'crn',
        label: 'CRN',
        type: 'number',
        inTable: false,
        sortable: false,
        searchable: true,
        value: (p) => p.crn,
        csv: (p) => p.crn || 'CRN not listed',
    },
    {
        key: 'instructor',
        label: 'Instructor',
        type: 'text',
        value: (p) => p.instructorName,
    },
    { key: 'awarded', label: 'Awarded', ...NUMBER, value: (p) => p.awarded },
    { key: 'filled', label: 'Filled', ...NUMBER, value: (p) => p.filled },
    { key: 'open', label: 'Open', ...NUMBER, value: (p) => p.openSlots },
];

const TABLE_COLUMNS = COLUMNS.filter((column) => column.inTable !== false);

// The table's columns and the candidates button's
const COLUMN_COUNT = TABLE_COLUMNS.length + 1;

const labelOf = (position) =>
    `${position.subject} ${position.courseNumber} section ${position.section}`;

/**
 * A program's positions for a term, one row each, each opening the
 * candidates for that section. The candidates are requested only when a row
 * is opened.
 */
export default function CoordinatorPositionsTab({
    programType,
    term,
    termName = null,
    decisionsReleased = null,
    onPositionChanged = null,
    show = null,
    onClearShow = null,
}) {
    const id = useId();
    const [view, setView] = useTabParam('positions', 'view');
    // `?open=<applicationId>` opens that section's candidates, as the
    // request page's Review candidates link asks
    const [searchParams, setSearchParams] = useSearchParams();
    const openParam = searchParams.get('open');
    const locked = usePreviewLock();
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(Boolean(term));
    const [failed, setFailed] = useState(false);
    const [openIds, setOpenIds] = useState(() => new Set());
    const [poolAdds, setPoolAdds] = useState(0);
    const requestRef = useRef(0);
    const openedRef = useRef(null);
    const [scrollTo, setScrollTo] = useState(null);
    const program = programType.toUpperCase();
    const asCards = useMediaQuery(CARDS_QUERY);
    // The list an Overview link asked for, filtered by the server's own
    // attention counts on each position
    const shownList = listFor(POSITION_LISTS, show);
    const listed = shownList ? positions.filter(shownList.matches) : positions;
    const controls = useTableControls({
        rows: listed,
        columns: COLUMNS,
        statusOf: placesOf,
        persist: useTableParams('positions'),
    });
    const { setSearch, setStatus } = controls;

    // A list an Overview link opened starts from exactly the rows it counted
    useEffect(() => {
        if (show) {
            setSearch('');
            setStatus('all');
        }
    }, [show, setSearch, setStatus]);

    const load = useCallback(async () => {
        const request = ++requestRef.current;
        setLoading(true);
        setFailed(false);
        try {
            const answer = await positionService.listPositions({
                program: programType,
                term,
            });
            if (request !== requestRef.current) return;
            setPositions(answer.positions || []);
        } catch (_err) {
            if (request === requestRef.current) setFailed(true);
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    }, [programType, term]);

    useEffect(() => {
        setOpenIds(new Set());
        if (!term) {
            setPositions([]);
            setLoading(false);
            return undefined;
        }
        load();
        return () => {
            requestRef.current += 1;
        };
    }, [term, load]);

    // Opens the section a link named once its row has loaded, then drops
    // `open` from the URL so a later refresh or collapse is left alone
    useEffect(() => {
        if (!openParam || openedRef.current === openParam) return;
        if (!positions.some((p) => p.applicationId === openParam)) return;
        openedRef.current = openParam;
        setOpenIds((prev) => new Set(prev).add(openParam));
        setScrollTo(openParam);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('open');
                return next;
            },
            { replace: true }
        );
    }, [openParam, positions, setSearchParams]);

    // Scrolls once the linked row is revealed
    useEffect(() => {
        if (!scrollTo || !openIds.has(scrollTo)) return;
        document
            .getElementById(`${id}-${scrollTo}`)
            ?.scrollIntoView?.({ block: 'nearest' });
        setScrollTo(null);
    }, [scrollTo, openIds, id]);

    const refreshPosition = useCallback(
        async (applicationId) => {
            try {
                const fresh = await positionService.getPosition(
                    applicationId,
                    programType
                );
                if (!fresh?.applicationId) return;
                setPositions((prev) =>
                    prev.map((p) =>
                        p.applicationId === applicationId ? fresh : p
                    )
                );
                if (onPositionChanged) onPositionChanged(fresh);
            } catch (_err) {
                // The row keeps its last counts; the next load corrects them.
            }
        },
        [programType, onPositionChanged]
    );

    const toggle = (applicationId) =>
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(applicationId)) next.delete(applicationId);
            else next.add(applicationId);
            return next;
        });

    let body;
    if (!term) {
        body = <p>Select a term to see its positions.</p>;
    } else if (loading) {
        body = (
            <div className="py-3" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading positions...
            </div>
        );
    } else if (failed) {
        body = (
            <Alert variant="danger">
                <p className="mb-2">The positions couldn&apos;t be loaded.</p>
                <Button variant="outline-danger" size="sm" onClick={load}>
                    Retry
                </Button>
            </Alert>
        );
    } else if (listed.length === 0) {
        body = <p>{`No ${program} positions for this term.`}</p>;
    } else {
        const toggleOf = (position, open, rowId) => (
            <Button
                variant="outline-primary"
                size="sm"
                className="text-nowrap"
                aria-expanded={open}
                aria-controls={rowId}
                aria-label={`${open ? 'Hide' : 'Show'} candidates for ${labelOf(position)}`}
                onClick={() => toggle(position.applicationId)}
            >
                Candidates
            </Button>
        );
        body = (
            <>
                <div className="mb-3">
                    <TableControlsBar
                        controls={controls}
                        noun="positions"
                        searchLabel="Search positions by course, section, CRN or instructor"
                        searchPlaceholder="Search by course, section, CRN, or instructor..."
                        statusLabel="Filter positions by available places"
                        allLabel="All positions"
                        statusOptions={PLACES_OPTIONS}
                        exportFilename={`${programType}-positions-${term}.csv`}
                    />
                    <SortBySelect
                        controls={controls}
                        className="d-lg-none mt-2 positions-sort"
                    />
                </div>
                <div className="table-responsive position-relative">
                    <Table
                        className="align-middle positions-table"
                        role="table"
                    >
                        <caption>{`${program} positions for ${termName || term}`}</caption>
                        <thead role="rowgroup">
                            <tr role="row">
                                {TABLE_COLUMNS.map((column) => (
                                    <SortableTh
                                        key={column.key}
                                        column={column}
                                        controls={controls}
                                        interactive={!asCards}
                                        role="columnheader"
                                    />
                                ))}
                                <th scope="col" role="columnheader">
                                    <span className="visually-hidden">
                                        Candidates
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody role="rowgroup">
                            {controls.rows.map((position) => {
                                const open = openIds.has(
                                    position.applicationId
                                );
                                const rowId = `${id}-${position.applicationId}`;
                                return (
                                    <React.Fragment
                                        key={position.applicationId}
                                    >
                                        <tr
                                            className="positions-row"
                                            role="row"
                                        >
                                            <td
                                                role="cell"
                                                className="positions-course"
                                                data-label="Course and section"
                                            >
                                                {`${courseCodeOf(position)}, section ${position.section}`}
                                                <span className="d-block small text-body-secondary">
                                                    {[
                                                        position.title,
                                                        position.crn
                                                            ? `CRN ${position.crn}`
                                                            : 'CRN not listed',
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                </span>
                                                {statusNoteOf(position) && (
                                                    <span className="d-block small fw-semibold">
                                                        {statusNoteOf(position)}
                                                    </span>
                                                )}
                                            </td>
                                            <td
                                                role="cell"
                                                data-label="Instructor"
                                            >
                                                {position.instructorName}
                                            </td>
                                            <td
                                                role="cell"
                                                data-label="Awarded"
                                                className="table-num"
                                            >
                                                {position.awarded}
                                            </td>
                                            <td
                                                role="cell"
                                                data-label="Filled"
                                                className="table-num"
                                            >
                                                {position.filled}
                                            </td>
                                            <td
                                                role="cell"
                                                data-label="Open"
                                                className="table-num"
                                            >
                                                {position.openSlots}
                                            </td>
                                            <td
                                                role="cell"
                                                className="positions-action"
                                            >
                                                {toggleOf(
                                                    position,
                                                    open,
                                                    rowId
                                                )}
                                            </td>
                                        </tr>
                                        <tr
                                            id={rowId}
                                            className="positions-detail"
                                            role="row"
                                            hidden={!open}
                                        >
                                            {open && (
                                                <td
                                                    role="cell"
                                                    colSpan={COLUMN_COUNT}
                                                >
                                                    <PositionPipeline
                                                        key={poolAdds}
                                                        applicationId={
                                                            position.applicationId
                                                        }
                                                        program={programType}
                                                        mode="coordinator"
                                                        releaseDate={
                                                            decisionsReleased
                                                        }
                                                        headingLevel={3}
                                                        onChanged={() =>
                                                            refreshPosition(
                                                                position.applicationId
                                                            )
                                                        }
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
                <TableEmpty
                    controls={controls}
                    noun="positions"
                    emptyText="No positions to show."
                />
            </>
        );
    }

    return (
        <div>
            <h2 className="h5">Positions</h2>
            {programType === 'la' && (
                <GuideLink
                    audience="coordinator"
                    className="d-block small mb-3"
                    short
                />
            )}
            <PreviewLockNote locked={locked} />
            <Tabs
                id={`${id}-views`}
                activeKey={view}
                onSelect={setView}
                variant="pills"
                className="mb-3 positions-views"
            >
                <Tab eventKey="positions" title="Awarded sections">
                    <p>
                        {`Each row is a section the ${program} program has awarded. Open one to review the students who applied to it, the students the faculty member named, and make offers.`}
                    </p>
                    {shownList && !loading && !failed && (
                        <ListFilterNote
                            show={show}
                            list={shownList}
                            count={listed.length}
                            programLabel={program}
                            termName={termName || term}
                            noun="sections"
                            onClear={onClearShow}
                        />
                    )}
                    {body}
                </Tab>
                <Tab eventKey="pool" title="General pool" mountOnEnter>
                    <GeneralPool
                        programType={programType}
                        term={term}
                        headingLevel={3}
                        onAdded={(position) => {
                            setPoolAdds((n) => n + 1);
                            if (position?.applicationId) {
                                refreshPosition(position.applicationId);
                            }
                        }}
                    />
                </Tab>
            </Tabs>
        </div>
    );
}
